import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { PublicationContext } from "../../../../context/PublicationContext";
import { ButtonsRight } from "../../../../interfaces";
import { Play, Pause, SkipBack, SkipForward, RotateCcw } from "lucide-react";
import { validateImageHttp } from "../../../../helpers";
import { usePublication } from "../../../../hooks/usePublication";
import { Link, useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import { findTypeOption } from "../../../../interfaces/Publication";
import { text } from "stream/consumers";
import { usePodcastPlayer } from "../../../../context/player/Player";

const BAR_COUNT = 35;

/** Genera alturas porcentuales variadas (8%..100%) */
const generateHeights = (count: number): number[] => {
  const arr: number[] = [];
  for (let i = 0; i < count; i++) {
    const base =
      28 + Math.round(72 * Math.abs(Math.sin((i / count) * Math.PI * 2.3 + i)));
    const jitter = ((i * 37) % 11) - 5;
    const h = Math.max(8, Math.min(100, base + jitter));
    arr.push(h);
  }
  return arr;
};
const BASE_HEIGHTS = generateHeights(BAR_COUNT);

// Color de las barras
const stylesColor = {
  filled: "#55B5DB",
  unfilled: "#9B9B9B",
  dragging: "#55B5DB",
};

const formatTime = (s: number) => {
  if (!isFinite(s) || isNaN(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${sec}`;
};

interface Props {
  openButtons: ButtonsRight;
  setOpenButtons: (button: ButtonsRight) => void;
}

export const Podcast = ({ openButtons, setOpenButtons }: Props) => {
  const { publication } = useContext(PublicationContext);
  const { sections, currentSection, path } = usePublication();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barsRef = useRef<HTMLDivElement | null>(null);
  const podcastContainerRef = useRef<HTMLDivElement | null>(null); // Nueva referencia para el scroll
  const isDraggingRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number>(0);
  const lastClickTimeRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [current, setCurrent] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [rate, setRate] = useState<number>(1);
  const [currentPodcastSection, setCurrentPodcastSection] = useState<any>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragProgress, setDragProgress] = useState<number>(0);
  const [animatedProgress, setAnimatedProgress] = useState<number>(0);
  const [isFromClick, setIsFromClick] = useState<boolean>(false);
  const { playerState } = usePodcastPlayer();

  // Obtener todas las secciones que tienen podcastURL
  const podcastSections = useMemo(() => {
    return sections.filter(
      (section) => section.podcastURL && section.podcastURL.trim() !== "",
    );
  }, [sections]);

  // Lógica mixta: si currentSection tiene podcast, usar ese, sino usar el primero disponible
  useEffect(() => {
    if (podcastSections.length > 0) {
      if (currentSection?.podcastURL) {
        // Si la sección actual tiene podcast, usar esa sección
        setCurrentPodcastSection(currentSection);
      } else {
        // Si la sección actual NO tiene podcast, usar el primero disponible
        setCurrentPodcastSection(podcastSections[0]);
      }
    } else {
      setCurrentPodcastSection(null);
    }
  }, [currentSection, podcastSections]);

  // Función para hacer scroll hacia arriba dentro del componente
  const scrollToTop = () => {
    if (podcastContainerRef.current) {
      podcastContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Encontrar la siguiente sección con podcast
  const findNextPodcastSection = () => {
    if (!currentPodcastSection || podcastSections.length === 0) return null;

    const currentIndex = podcastSections.findIndex(
      (section) => section.id === currentPodcastSection.id,
    );

    if (currentIndex < podcastSections.length - 1) {
      return podcastSections[currentIndex + 1];
    }
    return null;
  };

  // Encontrar la sección anterior con podcast
  const findPrevPodcastSection = () => {
    if (!currentPodcastSection || podcastSections.length === 0) return null;

    const currentIndex = podcastSections.findIndex(
      (section) => section.id === currentPodcastSection.id,
    );

    if (currentIndex > 0) {
      return podcastSections[currentIndex - 1];
    }
    return null;
  };

  // Encontrar el índice de la sección en el arreglo original
  const findSectionIndex = (section: any) => {
    return sections.findIndex((s) => s.id === section?.id);
  };

  const nextPodcastSection = findNextPodcastSection();
  const prevPodcastSection = findPrevPodcastSection();

  const nextSectionIndex = findSectionIndex(nextPodcastSection);
  const prevSectionIndex = findSectionIndex(prevPodcastSection);

  const isNextDisabled = !nextPodcastSection;
  const isPrevDisabled = !prevPodcastSection;

  // Animación suave del progreso - solo cuando no es desde click
  useEffect(() => {
    if (isDragging) {
      setAnimatedProgress(dragProgress);
      return;
    }

    // Si es desde click, no animar - llenar instantáneamente
    if (isFromClick) {
      setAnimatedProgress(current);
      const timeout = setTimeout(() => {
        setIsFromClick(false);
      }, 100);
      return () => clearTimeout(timeout);
    }

    let startTime: number;
    let startProgress = animatedProgress;
    let targetProgress = current;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      // Duración de la animación en ms (más suave)
      const durationMs = 800;
      const progress = Math.min(elapsed / durationMs, 1);

      // Función de easing suave
      const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

      const newProgress =
        startProgress + (targetProgress - startProgress) * easeOut(progress);
      setAnimatedProgress(newProgress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [current, isDragging, dragProgress, isFromClick]);

  // cargar metadata y timeupdate
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      const d = audio.duration;
      setDuration(isFinite(d) ? d : 0);
    };
    const onTime = () => {
      if (!isDraggingRef.current) {
        setCurrent(audio.currentTime);
      }
    };
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    setIsPlaying(false);
  }, [currentPodcastSection]);

  // play / pause
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    }
  };

  // skip seconds
  const skip = (secs: number) => {
    const audio = audioRef.current;
    if (!audio || !isFinite(duration) || duration <= 0) return;
    setIsFromClick(true);
    audio.currentTime = Math.max(
      0,
      Math.min(duration, audio.currentTime + secs),
    );
    setCurrent(audio.currentTime);
  };

  // restart
  const restart = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setIsFromClick(true);
    audio.currentTime = 0;
    setCurrent(0);
    setAnimatedProgress(0);
    if (!isPlaying) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {}
    }
  };

  // toggle playback rate
  const toggleRate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : rate === 1.5 ? 2 : 1;
    audio.playbackRate = next;
    setRate(next);
  };

  // Calcular progreso actual
  const currentProgress = isDragging ? dragProgress : animatedProgress;
  const percent =
    typeof duration === "number" && isFinite(duration) && duration > 0
      ? Math.min(1, Math.max(0, currentProgress / duration))
      : 0;

  const filledCount = Math.min(
    BAR_COUNT,
    Math.max(0, Math.floor(percent * BAR_COUNT)),
  );

  // Manejar inicio del arrastre
  const handleDragStart = (e: React.MouseEvent) => {
    const el = barsRef.current;
    if (!el) return;

    isDraggingRef.current = true;
    setIsDragging(true);

    const rect = el.getBoundingClientRect();

    // Función para calcular el tiempo basado en la posición del mouse
    const calculateTimeFromMouse = (clientX: number) => {
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      return ratio * duration;
    };

    const initialTime = calculateTimeFromMouse(e.clientX);
    setDragProgress(initialTime);

    // Agregar event listeners para el arrastre
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newTime = calculateTimeFromMouse(moveEvent.clientX);
      setDragProgress(newTime);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      // Calcular el tiempo final basado en la posición actual del mouse
      const finalTime = calculateTimeFromMouse(upEvent.clientX);

      // Aplicar el cambio al audio
      const audio = audioRef.current;
      if (audio && isFinite(duration) && duration > 0) {
        setIsFromClick(true);
        audio.currentTime = finalTime;
        setCurrent(finalTime);
        setAnimatedProgress(finalTime);
      }

      // Limpiar
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Click simple (sin arrastre) - llenado instantáneo
  const seekByClick = (e: React.MouseEvent) => {
    // Prevenir que se dispare tanto el click como el drag
    const now = Date.now();
    if (now - lastClickTimeRef.current < 200) return;
    lastClickTimeRef.current = now;

    const el = barsRef.current;
    const audio = audioRef.current;
    if (!el || !audio || !isFinite(duration) || duration <= 0) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const newTime = ratio * duration;

    setIsFromClick(true);
    audio.currentTime = newTime;
    setCurrent(newTime);
  };

  const section = publication.sections[publication.currentSection];
  const urlCoverImg = useMemo(
    () => validateImageHttp(publication.coverImg, path),
    [path, publication.coverImg],
  );

  const imageSectionCover = useMemo(() => {
    if (currentPodcastSection?.cover) {
      return validateImageHttp(currentPodcastSection.cover, path);
    }

    if (currentSection?.cover) {
      return validateImageHttp(currentSection.cover, path);
    }

    if (section?.cover) {
      return validateImageHttp(section.cover, path);
    }

    return `${urlCoverImg}`;
  }, [
    currentPodcastSection?.cover,
    currentSection?.cover,
    section?.cover,
    path,
  ]);

  // Cleanup animation frame
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const history = useNavigate();
  const route = findTypeOption("libro")?.route || "";

  const handleRedirect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRoute = event.target.value;
    history(selectedRoute);
  };

  return (
    <div
      id="option-14"
      className={`aside__content ${openButtons.optPodcast ? "active" : ""}`}
      style={{ paddingLeft: "10px", paddingRight: "20px" }}
    >
      <div className="aside__content-header">
        <h3>Podcast</h3>
        <button
          className="btn btn-rounded btn-rounded-transparent btn-close"
          onClick={() => {
            setOpenButtons({ ...openButtons, optPodcast: false });
          }}
        >
          <span className="btn__icon">
            <i className="icon icon-vpro-close"></i>
          </span>
        </button>
      </div>
      <div
        className="aside__content-data chap-index main-sb"
        ref={podcastContainerRef}
        style={{
          overflowY: "auto",
          height: "100%",
          padding: 0,
          textAlign: "left",
        }}
      >
        <div style={styles.root}>
          {/* inline CSS: keyframes + any global small rules */}
          <style>{styles.inlineCss}</style>

          {/* main card */}
          <div style={styles.card}>
            {/* top select pill */}
            <div style={styles.topRow}>
              <div style={styles.selectWrap}>
                <select
                  className="select-dd__select"
                  onChange={handleRedirect}
                  value={`/${route}/${publication.id}/${publication.currentSection}`}
                  style={styles.select}
                  aria-label="Seleccionar capítulo"
                >
                  {sections.map((section, index) => (
                    <>
                      {section.podcastURL && (
                        <option
                          key={index}
                          value={`/libros/${publication.id}/${index}`}
                        >
                          {parse(section.title)}
                        </option>
                      )}
                    </>
                  ))}
                </select>
                <span className="select-dd__arrow"></span>
              </div>
            </div>

            {currentPodcastSection && currentPodcastSection.title && (
              <h3 style={styles.cardTitle}>
                {currentPodcastSection.title || "Capítulo"}
              </h3>
            )}

            {/* cover + speaking border animation */}
            <div style={styles.coverWrap}>
              {/* Border animado tipo "AI speaking" */}
              {isPlaying && (
                <>
                  <div style={styles.speakingBorder1} />
                  <div style={styles.speakingBorder2} />
                  <div style={styles.speakingBorder3} />
                </>
              )}
              <img src={imageSectionCover} alt="cover" style={styles.cover} />
            </div>

            <div style={styles.meta}>
              {publication && publication.title && (
                <div style={styles.metaTitle}>
                  {publication.title || "Título"}
                </div>
              )}
              <div className="metaSub" style={styles.metaSub}>
                Por Virtualpro
              </div>
            </div>

            <audio
              ref={audioRef}
              src={currentPodcastSection?.podcastURL}
              preload="metadata"
            />

            {/* progress: time left | bars grid | duration */}
            <div style={styles.progressRow}>
              <div style={styles.timeLeft}>{formatTime(currentProgress)}</div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${BAR_COUNT}, 1fr)`,
                  alignItems: "center",
                  gap: 4,
                  height: 60,
                  width: "100%",
                  maxWidth: 600,
                  padding: "0 2px",
                  borderRadius: 8,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onMouseDown={handleDragStart}
                onClick={seekByClick}
                ref={barsRef}
                role="slider"
                aria-label="Barra de progreso del audio"
                aria-valuenow={percent * 100}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {BASE_HEIGHTS.map((h, i) => {
                  const isFilled = i < filledCount;
                  const isPartiallyFilled = i === filledCount;
                  const currentColor = isDragging
                    ? stylesColor.dragging
                    : stylesColor.filled;

                  // Para barras completamente llenas
                  if (isFilled) {
                    return (
                      <div
                        key={i}
                        style={{
                          ...styles.singleBar,
                          height: `${h}%`,
                          background: currentColor,
                          borderRadius: 4,
                          transition: isFromClick
                            ? "background-color 50ms ease, height 200ms ease"
                            : "background-color 200ms ease, height 200ms ease",
                        }}
                      />
                    );
                  }

                  // Para la barra parcialmente llena (solo cuando NO es desde click)
                  if (isPartiallyFilled && !isFromClick) {
                    const fillPercentage =
                      (percent * BAR_COUNT - filledCount) * 100;
                    return (
                      <div
                        key={i}
                        style={{
                          ...styles.singleBar,
                          height: `${h}%`,
                          backgroundImage: `linear-gradient(90deg, ${currentColor} 0%, ${currentColor} ${fillPercentage}%, ${stylesColor.unfilled} ${fillPercentage}%, ${stylesColor.unfilled} 100%)`,
                          borderRadius: 4,
                          transition:
                            "background-image 100ms ease, height 200ms ease",
                        }}
                      />
                    );
                  }

                  // Para barras vacías o cuando es desde click (no mostrar barra parcial)
                  return (
                    <div
                      key={i}
                      style={{
                        ...styles.singleBar,
                        height: `${h}%`,
                        background: stylesColor.unfilled,
                        borderRadius: 4,
                        transition:
                          "background-color 200ms ease, height 200ms ease",
                      }}
                    />
                  );
                })}
              </div>

              <div style={styles.timeRight}>{formatTime(duration)}</div>
            </div>

            {/* controls */}
            <div style={styles.controlsRow}>
              <div
                style={styles.rateBtn}
                onClick={toggleRate}
                aria-label="Cambiar velocidad"
              >
                {rate}x
              </div>

              <Link
                to={
                  !isPrevDisabled
                    ? `/${route}/${publication.id}/${prevSectionIndex}`
                    : "#"
                }
                style={styles.iconBtn}
                type="button"
                className={`optionsAudioPodcast ${isPrevDisabled ? "disabled" : ""}`}
                data-tippy-content="Capítulo anterior"
                onClick={scrollToTop}
              >
                <SkipBack size={18} />
              </Link>

              <button
                style={styles.playBtn}
                onClick={togglePlay}
                aria-label={isPlaying ? "Pausar" : "Reproducir"}
              >
                {isPlaying ? (
                  <Pause
                    size={30}
                    color="black"
                    style={{ fill: "white", stroke: "white" }}
                  />
                ) : (
                  <Play
                    size={30}
                    color="black"
                    style={{ fill: "white", stroke: "white" }}
                  />
                )}
              </button>

              <Link
                to={
                  !isNextDisabled
                    ? `/${route}/${publication.id}/${nextSectionIndex}`
                    : "#"
                }
                style={styles.iconBtn}
                className={`optionsAudioPodcast ${isNextDisabled ? "disabled" : ""}`}
                onClick={scrollToTop}
              >
                <SkipForward size={18} />
              </Link>

              <button
                style={styles.iconBtn}
                onClick={restart}
                className="optionsAudioPodcast"
                title="Volver al inicio"
              >
                <RotateCcw size={18} />
              </button>
            </div>

            <div style={styles.otherTitle}>Otros podcasts de este libro</div>
          </div>

          {/* other episodes */}
          <div className="ai-chat__in" style={styles.otherList}>
            {publication.sections
              .filter((section) => section.podcastURL)
              .map((section, index, filteredArray) => {
                const isLast = index === filteredArray.length - 1;
                const isFirst = index === 0;

                const originalIndex = publication.sections.findIndex(
                  (s) => s.id === section.id,
                );
                const isCurrentlyPlaying =
                  currentPodcastSection?.id === section.id;

                return (
                  <Link
                    to={`/${route}/${publication.id}/${originalIndex}`}
                    key={`podcast-${originalIndex}`}
                    className={`episodeRow ${isCurrentlyPlaying ? "episodeTitlePlaying" : "episodeTitle"}`}
                    style={{
                      ...styles.episodeRow,
                      ...(isLast && styles.lastEpisodeRow),
                      ...(isFirst && styles.firstEpisodeRow),
                    }}
                    onClick={scrollToTop}
                  >
                    <img
                      src={`${
                        section.cover
                          ? `https://polifoniaeditores.com/v/verbo7api/public/publications/0/4/images/${section.cover}`
                          : `${urlCoverImg}`
                      }`}
                      alt="Capítulo podcast"
                      style={styles.episodeImg}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {/* Equalizador animado - ahora al lado izquierdo del título */}
                        {isCurrentlyPlaying && isPlaying && (
                          <div className="audio-equalizer">
                            <div className="equalizer-bar" />
                            <div className="equalizer-bar" />
                            <div className="equalizer-bar" />
                          </div>
                        )}
                        <div
                          className={isCurrentlyPlaying ? "episodeTitlePlaying" : "episodeTitle"}
                          style={{
                            ...styles.episodeTitle,
                            ...(isCurrentlyPlaying && { color: "#FFA500" }),
                          }}
                        >
                          {section.title}
                        </div>
                      </div>
                      <div style={styles.episodeSub}>{section.description}</div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: any } = {
  // ... (todos los estilos se mantienen exactamente igual)
  inlineCss: `
    /* speaking border animations */
    @keyframes speaking-pulse-1 {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
    }
    
    @keyframes speaking-pulse-2 {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
      100% { transform: translate(-50%, -50%) scale(1.3); opacity: 0; }
    }
    
    @keyframes speaking-pulse-3 {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
      100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
    }
    
    /* small global rule */
    * { box-sizing: border-box; }
  `,
  root: {
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    fontFamily: '"Heebo", Helvetica, sans-serif',
    letterSpacing: "0.5px",
  },

  topRow: {
    width: "100%",
    maxWidth: 540,
    justifyContent: "flex-start",
    marginBottom: 30,
  },
  selectWrap: {
    marginTop: 3,
    position: "relative",
  },
  select: {
    appearance: "none",
    WebkitAppearance: "none",
    width: "100%",
    minWidth: 280,
    borderRadius: 999,
    padding: "10px 36px 10px 20px",
    border: "1px solid #e6eeef",
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 5px 4px rgba(0, 0, 0, 0.08), 0 1px 1px rgba(0, 0, 0, 0.04)",
  },
  chevronInSelect: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  lastEpisodeRow: {
    borderRadius: "0 0 40px 40px",
    paddingBottom: "25px",
  },
  firstEpisodeRow: {
    paddingTop: 17,
  },

  card: {
    width: "100%",
    maxWidth: 540,
    borderRadius: "40px 40px 0 0",
    padding: 18,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  },
  cardTitle: { margin: 0, fontSize: 17, fontWeight: 700, textAlign: "center" },

  coverWrap: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 14,
    marginBottom: 10,
  },
  // Speaking border animations (tipo apps de IA)
  // Speaking border animations (más cerca de la imagen)
  speakingBorder1: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 158, // 160px (imagen) + 4px (borde) = 164px + 2px de separación
    height: 158,
    borderRadius: "50%",
    border: "2px solid #BCBCBC",
    opacity: 1,
    animation: "speaking-pulse-1 2s infinite ease-out",
    pointerEvents: "none",
  },
  speakingBorder2: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 158, // Mismo tamaño inicial que el primero
    height: 158,
    borderRadius: "50%",
    border: "2px solid #BCBCBC",
    opacity: 0.7,
    animation: "speaking-pulse-2 2s infinite ease-out 0.3s",
    pointerEvents: "none",
  },
  speakingBorder3: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 158, // Mismo tamaño inicial que los demás
    height: 158,
    borderRadius: "50%",
    border: "2px solid #BCBCBC",
    opacity: 0.4,
    animation: "speaking-pulse-3 2s infinite ease-out 0.6s",
    pointerEvents: "none",
  },
  cover: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    objectFit: "cover",
    border: "4px solid rgba(229, 231, 235, 1)",
    zIndex: 2,
    display: "block",
  },

  meta: { textAlign: "center" },
  metaTitle: { fontSize: 17, fontWeight: 700, marginBottom: 4 },
  metaSub: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 20,
  },

  progressRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 8,
    marginBottom: 8,
  },
  timeLeft: {
    width: 50,
    textAlign: "left",
    fontSize: 12,
    color: "#55B5DB",
    fontWeight: 700,
  },
  timeRight: {
    width: 50,
    textAlign: "right",
    fontSize: 12,
    color: "#374151",
    fontWeight: 700,
  },

  // base per-bar styles
  singleBar: {
    width: "100%",
    alignSelf: "center",
    borderRadius: 4,
  },

  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 6,
  },
  rateBtn: {
    padding: "6px 10px",
    background: "transparent",
    cursor: "pointer",
    fontWeight: 700,
    border: "none",
  },
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#374151",
  },
  playBtn: {
    width: 60,
    height: 60,
    borderRadius: 999,
    background: "#111827",
    color: "#fff",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  otherList: {
    width: "100%",
    maxWidth: 540,
    flexDirection: "column",
    gap: 10,
    marginTop: -24,
    borderRadius: "0 0 40px 40px",
    padding: 14,
  },
  otherTitle: {
    fontSize: 15,
    marginTop: 35,
    fontWeight: 700,
    textAlign: "left",
    width: "100%",
  },

  episodeRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "10px 20px",
    lineHeight: "17px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  episodeImg: { width: 44, height: 44, objectFit: "cover" },
  episodeTitle: { fontSize: 14, fontWeight: 700 },
  episodeSub: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: "12px",
    marginTop: 6,
  },
};
